import React, { useCallback, useState } from 'react';
import { ProjectManagementStatus, Release, patchRelease } from '@round/api';
import { showNotification } from 'helpers';

type Result = {
    status: ProjectManagementStatus | null;
    updateStatus: (next: ProjectManagementStatus) => Promise<void>;
    isUpdating: boolean;
};

export function useProjectManagementStatus(
    release: Release | null,
    setRelease: React.Dispatch<React.SetStateAction<Release | null>>
): Result {
    const [isUpdating, setIsUpdating] = useState(false);
    const status = release?.project_management_status ?? null;

    const updateStatus = useCallback(
        async (next: ProjectManagementStatus) => {
            if (!release) {
                return;
            }

            const previous = release.project_management_status;
            setRelease((prev) => (prev ? { ...prev, project_management_status: next } : prev));
            setIsUpdating(true);
            try {
                const response = await patchRelease(release.id, {
                    project_management_status: next,
                });
                if (response.status === 200) {
                    setRelease(response.data);
                } else {
                    setRelease((prev) => (prev ? { ...prev, project_management_status: previous } : prev));
                    const error = response.status === 400 ? response.data.project_management_status : undefined;
                    showNotification(
                        (Array.isArray(error) ? error.join(' ') : error) || 'Could not update project status',
                        'error'
                    );
                }
            } catch {
                setRelease((prev) => (prev ? { ...prev, project_management_status: previous } : prev));
                showNotification('Could not update project status', 'error');
            } finally {
                setIsUpdating(false);
            }
        },
        [release, setRelease]
    );

    return { status, updateStatus, isUpdating };
}
